import * as React from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import { API } from "aws-amplify";
import { SelectOption, matchesSelection } from "./feature-type-filter";
import FeatureTypeFilters from "./FeatureTypeFilters";

interface InventoryItem {
  featureName: string;
  customerTypeName: string;
  readyCount: number;
  newCount: number;
  invalidCount: number;
  minimumThreshold: number;
  enabled?: boolean;
}

type SortKey = keyof InventoryItem | 'health';

interface State {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;
  filterFeature: SelectOption | null;
  filterType: SelectOption | null;
  sortBy: SortKey;
  sortAsc: boolean;
  countdown: number;
}

class InventoryDashboard extends React.Component<any, State> {
  private timer: any = null;

  private static readonly HEALTH_THRESHOLDS = {
    GOOD: 0.7,
    WARNING: 0.3,
  } as const;

  constructor(props: any) {
    super(props);
    this.state = {
      items: [],
      loading: true,
      error: null,
      filterFeature: null,
      filterType: null,
      sortBy: 'featureName',
      sortAsc: true,
      countdown: 60,
    };
  }

  componentDidMount() {
    this.loadInventory();
    this.timer = setInterval(() => {
      this.setState(prev => {
        if (prev.countdown <= 1) {
          this.loadInventory();
          return { countdown: 60 };
        }
        return { countdown: prev.countdown - 1 };
      });
    }, 1000);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  loadInventory = async () => {
    try {
      const result = await API.get("CpcpV2API", "/cpcp/v2/inventory", {});
      this.setState({ items: result.items || [], loading: false, error: null, countdown: 60 });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load inventory", loading: false });
    }
  };

  getStatus = (ready: number, threshold: number): "success" | "info" | "error" => {
    if (threshold <= 0) return "success";
    const pct = ready / threshold;
    if (pct >= InventoryDashboard.HEALTH_THRESHOLDS.GOOD) return "success";
    if (pct >= InventoryDashboard.HEALTH_THRESHOLDS.WARNING) return "info";
    return "error";
  };

  render() {
    const { items, loading, error, filterFeature, filterType, sortBy, sortAsc, countdown } = this.state;

    if (loading) return <Spinner size="large" />;

    const selection = { feature: filterFeature, type: filterType };
    const filtered = items
      .filter(i => matchesSelection(i, selection))
      .sort((a, b) => {
        let av: number | string | boolean | undefined, bv: number | string | boolean | undefined;
        if (sortBy === 'health') {
          av = a.minimumThreshold > 0 ? a.readyCount / a.minimumThreshold : 1;
          bv = b.minimumThreshold > 0 ? b.readyCount / b.minimumThreshold : 1;
        } else {
          av = a[sortBy];
          bv = b[sortBy];
        }
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        let cmp: number;
        if (typeof av === 'boolean') cmp = Number(av) - Number(bv as boolean);
        else if (typeof av === 'number') cmp = av - (bv as number);
        else cmp = String(av).localeCompare(String(bv));
        return sortAsc ? cmp : -cmp;
      });

    return (
      <SpaceBetween size="l">
        <Header variant="h2">Inventory Dashboard</Header>
        {error && <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />}

        <FeatureTypeFilters
          items={items}
          selection={{ feature: filterFeature, type: filterType }}
          onChange={next => this.setState({ filterFeature: next.feature, filterType: next.type })}
          placeholder="All"
        />

        <Box>
          <SpaceBetween size="xs" direction="horizontal">
            <Box fontWeight="bold" fontSize="body-s">Legend:</Box>
            <StatusIndicator type="success">≥ 70% of threshold</StatusIndicator>
            <StatusIndicator type="info">≥ 30% of threshold</StatusIndicator>
            <StatusIndicator type="error">&lt; 30% of threshold</StatusIndicator>
          </SpaceBetween>
        </Box>

        <Table
          header={
            <Header
              counter={`(${filtered.length}/${items.length})`}
              actions={
                <SpaceBetween size="xs" direction="horizontal">
                  <Box color="text-body-secondary" fontSize="body-s" padding={{ top: "xs" }}>
                    Refreshing in: {countdown}s
                  </Box>
                  <Button iconName="refresh" onClick={this.loadInventory} />
                </SpaceBetween>
              }>
              Inventory Dashboard
            </Header>
          }
          sortingColumn={{ sortingField: sortBy }}
          sortingDescending={!sortAsc}
          onSortingChange={({ detail }) => {
            const field = detail.sortingColumn.sortingField as SortKey | undefined;
            if (!field) return;
            this.setState(prev => ({
              sortBy: field,
              sortAsc: prev.sortBy === field ? !prev.sortAsc : true,
            }));
          }}
          columnDefinitions={[
            { id: "featureName", header: "Feature Name", cell: (item: InventoryItem) => item.featureName, sortingField: "featureName" },
            { id: "customerTypeName", header: "Customer Type Name", cell: (item: InventoryItem) => <b>{item.customerTypeName}</b>, sortingField: "customerTypeName" },
            { id: "enabled", header: "Enabled", cell: (item: InventoryItem) => {
              if (item.enabled == null) return "—";
              return <StatusIndicator type={item.enabled ? "success" : "stopped"}>{item.enabled ? "Yes" : "No"}</StatusIndicator>;
            }, sortingField: "enabled" },
            { id: "readyCount", header: "Ready", cell: (item: InventoryItem) => (
              <StatusIndicator type={this.getStatus(item.readyCount, item.minimumThreshold)}>
                {item.readyCount}
              </StatusIndicator>
            ), sortingField: "readyCount" },
            { id: "threshold", header: "Threshold", cell: (item: InventoryItem) => item.minimumThreshold, sortingField: "minimumThreshold" },
            { id: "inFlight", header: "In-Flight", cell: (item: InventoryItem) => item.newCount, sortingField: "newCount" },
            { id: "health", header: "Health", cell: (item: InventoryItem) => {
              const pct = item.minimumThreshold > 0
                ? Math.min(100, Math.round((item.readyCount / item.minimumThreshold) * 100))
                : 100;
              const status = this.getStatus(item.readyCount, item.minimumThreshold);
              const color = status === "success" ? "#1d8102" : status === "info" ? "#0972d3" : "#d13212";
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 8, backgroundColor: '#e9ebed', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 12, color, fontWeight: 600, minWidth: 35 }}>{pct}%</span>
                </div>
              );
            }, minWidth: 150, sortingField: "health" },
          ]}
          items={filtered}
          empty={<Box textAlign="center" color="inherit">No inventory data</Box>}
        />
      </SpaceBetween>
    );
  }
}

export default InventoryDashboard;
